import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch, useLogger, useSelector } from '~/Hooks';
import gql from 'graphql-tag';

import { SERVICE_TYPE } from '~../__queries__';
import { findMenuForCurrentTime } from '~utilities';
import { getVenueMenus, getVenueMenusVariables } from './__queries__';

const MENU_QUERY = gql`
  fragment MenuDetails on ActiveAppliedMenu {
    id
    availabilities {
      dayOfWeek
      timeRange {
        start
        end
      }
    }
    menu {
      id
      name
      menuEntries {
        id

        price
        instant

        item {
          id
          vatRate
        }
      }
    }
  }

  #  Seperated because service type does not exist in the menu on getAppliedMenus
  query getVenueMenus($companyId: String!, $venueId: String!) {
    TAKE_AWAY: getAppliedMenus(companyId: $companyId, venueId: $venueId, serviceType: TAKE_AWAY) {
      results {
        ...MenuDetails
      }
    }
    EAT_HERE: getAppliedMenus(companyId: $companyId, venueId: $venueId, serviceType: EAT_HERE) {
      results {
        ...MenuDetails
      }
    }
  }
`;

interface Props {
  serviceType: SERVICE_TYPE | null;
}

const MenuManager: React.FunctionComponent<Props> = ({ serviceType }) => {
  const companyId = useSelector((state) => state.company);
  const logger = useLogger('epos-menu-manager');
  const [, , venueId] = location.hash.split('/');

  const dispatch = useDispatch();
  const menuQuery = useQuery<getVenueMenus, getVenueMenusVariables>(MENU_QUERY, {
    variables: {
      companyId,
      venueId,
    },
    skip: !companyId || !venueId,
  });

  const takeAwayMenus =
    menuQuery.data?.TAKE_AWAY.results.map((menu) => ({
      ...menu,
      serviceType: 'TAKE_AWAY',
    })) ?? [];

  const eatHereMenus =
    menuQuery.data?.EAT_HERE.results.map((menu) => ({
      ...menu,
      serviceType: 'EAT_HERE',
    })) ?? [];

  const menus = [...takeAwayMenus, ...eatHereMenus];

  useEffect(() => {
    if (!menus) {
      return;
    }

    // If service type is not set, set the menu to false (This is in the start screen before the user has selected a service type)
    if (!serviceType) {
      dispatch({
        type: 'SET_POS_MENU',
        menu: false,
      });
      return;
    }

    const serviceTypeMenus = menus.filter((menu) => menu.serviceType === serviceType);

    // If there are no menus for the service type, set the menu to false, This is a faulty setup
    if (serviceTypeMenus.length === 0) {
      logger.warn('No menus found for service type', { serviceType });

      dispatch({
        type: 'SET_POS_MENU',
        menu: false,
      });
      return;
    }

    const hasAvailability = serviceTypeMenus.some((m) => m.availabilities?.length > 0);

    // If there are no availabilities for the service type, set the menu to the first menu
    if (!hasAvailability) {
      logger.info('Setting menu (not time controlled)', {
        serviceType,
        menu: serviceTypeMenus[0].menu,
      });
      dispatch({
        type: 'SET_POS_MENU',
        menu: serviceTypeMenus[0].menu ?? false,
      });

      return;
    }

    // Find the menu that is available for the current time
    const menuToUse = findMenuForCurrentTime(serviceTypeMenus);
    const foundDefaultMenu = serviceTypeMenus.find((menu) => menu.availabilities.length === 0);

    if (!menuToUse && foundDefaultMenu) {
      logger.info('Setting default menu', foundDefaultMenu);
      dispatch({
        type: 'SET_POS_MENU',
        menu: foundDefaultMenu.menu,
      });
      return;
    }

    logger.info('Setting timecontrolled menu', menuToUse);
    dispatch({
      type: 'SET_POS_MENU',
      menu: menuToUse?.menu ?? false,
    });
  }, [serviceType, menus]);

  return null;
};

export default React.memo(MenuManager);
